import Guid from "common/values/guid/guid";
import _ from "lodash";
import Forum from "messaging/entities/forum/forum";
import Topic from "messaging/values/topic";
import { Moment } from "moment";
import Proposal, { ProposalField } from "work/entities/proposal/proposal";
import { Masked } from "../work-agreement/work-agreement";
import Subscriber from "messaging/values/subcriber/subscriber";

export enum Audience {
  VendorReviewers = "vendorReviewers",
  ClientReviewers = "clientReviewers",
  AllReviewers = "allReviewers",
}

export default class CommentThread {
  id?: Guid;
  proposalId: Guid;
  field: ProposalField;
  isExternal: boolean;
  subscribers: Subscriber[];
  createdDate?: Moment;
  isResolved?: boolean;
  numUnread?: number;

  constructor(
    proposalId: Guid,
    field: ProposalField,
    subscribers: Subscriber[] = [],
    isExternal = false,
    id?: Guid,
    createdDate?: Moment,
    numUnread?: number
  ) {
    this.id = id;
    this.proposalId = proposalId;
    this.field = field;
    this.isExternal = isExternal;
    this.subscribers = subscribers;
    if (createdDate) {
      this.createdDate = createdDate;
    }
    this.numUnread = numUnread;
  }

  public static fromForum(forum: Forum): CommentThread {
    if (forum.topic?.entityClass !== "Work.Proposal") {
      throw new InvalidCommentThreadError("Forum is not a proposal forum");
    }
    if (!forum.topic?.entityId) {
      throw new InvalidCommentThreadError(
        "Proposal id is missing on comment forum"
      );
    }
    if (!forum.topic?.context) {
      throw new InvalidCommentThreadError(
        "Proposal context is missing on comment forum"
      );
    }
    const contextJSON = JSON.parse(forum.topic.context);
    const field = ProposalField.fromJSON(contextJSON.field);
    if (!field) {
      console.warn(`Invalid proposal field ${contextJSON.field}`);
    }

    // this likes to crash using array tools like contextJSON.audience.some
    const audiences = contextJSON.audience;
    let isExternal = false;
    if (audiences) {
      for (const audience of audiences) {
        if (audience.includes("all")) {
          isExternal = true;
          break;
        }
      }
    } else {
      console.warn(`Invalid proposal audience`);
    }

    return new CommentThread(
      forum.topic.entityId,
      field ?? contextJSON.field,
      forum.subscribers,
      isExternal,
      forum.id,
      forum.createdDate,
      forum.numUnreadMessages
    );
  }

  public getAudience(isViewingAsVendor: boolean): Audience {
    if (this.isExternal) {
      return Audience.AllReviewers;
    }
    return isViewingAsVendor
      ? Audience.VendorReviewers
      : Audience.ClientReviewers;
  }

  public toForum(isViewingAsVendor: boolean): Forum {
    let audience = isViewingAsVendor ? "vendorReviewers" : "clientReviewers";
    if (this.isExternal) {
      audience = "allReviewers";
    }
    return new Forum(
      `Proposal ${this.getAudience(
        isViewingAsVendor
      )} ${this.field.toString()} comment thread`,
      new Topic(
        "Work.Proposal",
        this.proposalId,
        JSON.stringify({
          field: this.field,
          audience: [audience],
        })
      ),
      this.subscribers,
      this.id,
      undefined,
      this.createdDate
    );
  }

  public static fromProposalField(
    proposal: Proposal,
    field: ProposalField,
    isViewingAsVendor: boolean,
    isExternal: boolean = false,
  ): CommentThread {
    if (!proposal.id) {
      throw new InvalidCommentThreadError("Proposal id is missing");
    }
    const subscribers = CommentThread.getSubscribersForProposal(
      isExternal,
      proposal,
      isViewingAsVendor
    );
    return new CommentThread(proposal.id, field, subscribers, isExternal);
  }

  public static getSubscribersForProposal(
    isExternal: boolean,
    proposal: Proposal,
    isViewingAsVendor: boolean
  ): Subscriber[] {
    let subscribers: Subscriber[];
    if (isExternal) {
      subscribers = CommentThread.getAllSubscribers(proposal);
    } else if (isViewingAsVendor) {
      subscribers = CommentThread.getVendorSubscribers(proposal);
    } else {
      subscribers = CommentThread.getClientSubscribers(proposal);
    }
    subscribers = _.uniqBy(subscribers, (sub) => sub.userId.value);
    return subscribers;
  }

  public static getClientSubscribers(proposal: Proposal): Subscriber[] {
    const clientSubscribers: Subscriber[] = [];
    if (proposal.client?.userId) clientSubscribers.push(new Subscriber(proposal.client.userId, proposal.client.entityId));
    clientSubscribers.push(
      ...proposal.clientReviewers.map((reviewer) => new Subscriber(reviewer.entityMember.userId, reviewer.entityMember.entityId))
    );
    return clientSubscribers;
  }

  public static getVendorSubscribers(proposal: Proposal): Subscriber[] {
    const vendorSubscribers: Subscriber[] = [];

    if (Masked.isMasked(proposal.vendorTeam)) {
      return vendorSubscribers;
    }

    if (
      proposal.vendorTeam?.leader &&
      proposal.vendorTeam.leader.userId &&
      proposal.vendorTeam.leader.entityId
    ) {
      vendorSubscribers.push(
        new Subscriber(
          proposal.vendorTeam.leader.userId,
          proposal.vendorTeam.leader.entityId
        )
      );
    }
    vendorSubscribers.push(
      ...proposal.vendorReviewers.map((reviewer) => new Subscriber(reviewer.entityMember.userId, reviewer.entityMember.entityId))
    );
    return vendorSubscribers;
  }

  public static getAllSubscribers(proposal: Proposal): Subscriber[] {
    const allSubscribers: Subscriber[] = [];
    if (proposal.client?.userId) allSubscribers.push(new Subscriber(proposal.client.userId, proposal.client.entityId));
    allSubscribers.push(
      ...proposal.clientReviewers.map((reviewer) => new Subscriber(reviewer.entityMember.userId, reviewer.entityMember.entityId))
    );

    if (Masked.isMasked(proposal.vendorTeam)) {
      return allSubscribers;
    }

    if (
      proposal.vendorTeam?.leader &&
      proposal.vendorTeam.leader.userId &&
      proposal.vendorTeam.leader.entityId
    ) {
      allSubscribers.push(
        new Subscriber(
          proposal.vendorTeam.leader.userId,
          proposal.vendorTeam.leader.entityId
        )
      );
    }
    allSubscribers.push(
      ...proposal.vendorReviewers.map((reviewer) => new Subscriber(reviewer.entityMember.userId, reviewer.entityMember.entityId))
    );
    return allSubscribers;
  }

  public isEqualTo(other: CommentThread): boolean {
    if (!other) return false;
    if (this.id && other.id) {
      return this.id.isEqualTo(other.id);
    } else if (
      this.proposalId.isEqualTo(other.proposalId) &&
      this.field.isEqualTo(other.field) &&
      this.isExternal === other.isExternal
    ) {
      return true;
    }
    return false;
  }
}

export class InvalidCommentThreadError extends Error {
  constructor(message: string) {
    super(message || `Invalid comment thread`);
  }
}
